import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { weekendBookletFaqData, FaqCategory } from '../data/weekend-booklet-faq-data';

// ── Pure filter function ──────────────────────────────────────

export function filterEntries(categories: FaqCategory[], searchTerm: string): FaqCategory[] {
  if (!searchTerm.trim()) return categories;

  const term = searchTerm.toLowerCase();
  return categories
    .map(category => ({
      ...category,
      entries: category.entries.filter(
        entry =>
          entry.question.toLowerCase().includes(term) ||
          entry.answer.toLowerCase().includes(term)
      ),
    }))
    .filter(category => category.entries.length > 0);
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '2rem',
    maxWidth: '900px',
    margin: '0 auto',
    backgroundColor: '#f5f7f5',
    minHeight: '100vh',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    color: '#1a6b3c',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: '1.5rem',
    textDecoration: 'none',
  },
  header: {
    marginBottom: '1.5rem',
  },
  title: {
    color: '#1a6b3c',
    fontSize: '1.75rem',
    margin: '0 0 0.5rem 0',
    fontWeight: 700,
  },
  description: {
    color: '#555',
    fontSize: '0.95rem',
    margin: 0,
    lineHeight: 1.5,
  },
  searchContainer: {
    marginBottom: '1.5rem',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  matchCount: {
    marginTop: '0.5rem',
    fontSize: '0.85rem',
    color: '#666',
  },
  noResults: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.95rem',
  },
  categoryNav: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginBottom: '2rem',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  categoryNavLink: {
    padding: '0.35rem 0.75rem',
    fontSize: '0.8rem',
    color: '#1a6b3c',
    backgroundColor: '#eef6f0',
    borderRadius: '4px',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: 500,
    border: '1px solid #d0e8d6',
    transition: 'background-color 0.15s',
  },
  categorySection: {
    marginBottom: '1.5rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  categoryTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    margin: '0 0 1rem 0',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #c9a84c',
    fontWeight: 600,
  },
  entryContainer: {
    borderBottom: '1px solid #eee',
  },
  entryQuestion: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.85rem 0',
    cursor: 'pointer',
    gap: '1rem',
  },
  entryQuestionText: {
    color: '#1a6b3c',
    fontSize: '0.95rem',
    fontWeight: 500,
    margin: 0,
    flex: 1,
  },
  chevron: {
    fontSize: '0.75rem',
    color: '#1a6b3c',
    transition: 'transform 0.2s ease',
    flexShrink: 0,
  },
  entryAnswer: {
    padding: '0 0 1rem 0',
    color: '#444',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    margin: 0,
  },
};

// ── Component ─────────────────────────────────────────────────

export function WeekendBookletFaqPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  const filteredCategories = filterEntries(weekendBookletFaqData, searchTerm);

  const totalMatchCount = filteredCategories.reduce(
    (sum, cat) => sum + cat.entries.length,
    0
  );

  const isSearchActive = searchTerm.trim().length > 0;

  const handleToggle = (entryId: string) => {
    setExpandedEntries(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const handleCategoryClick = (categoryId: string) => {
    const el = document.getElementById(`category-${categoryId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div style={styles.page}>
      {/* Back link */}
      <div
        style={styles.backLink}
        onClick={() => navigate('/weekend-booklet')}
        role="link"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') navigate('/weekend-booklet'); }}
      >
        ← Back to Weekend Booklet
      </div>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Weekend Booklet FAQ</h1>
        <p style={styles.description}>
          Find answers to common questions about the Weekend Booklet production process,
          from creating editions and managing submissions to print ordering and sales tracking.
        </p>
      </div>

      {/* Search */}
      <div style={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search FAQ..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={styles.searchInput}
          aria-label="Search FAQ entries"
        />
        {isSearchActive && (
          <div style={styles.matchCount}>
            {totalMatchCount} {totalMatchCount === 1 ? 'result' : 'results'} found
          </div>
        )}
      </div>

      {/* Category Navigation */}
      {!isSearchActive && (
        <div style={styles.categoryNav}>
          {weekendBookletFaqData.map(category => (
            <span
              key={category.id}
              style={styles.categoryNavLink}
              onClick={() => handleCategoryClick(category.id)}
              role="link"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter') handleCategoryClick(category.id); }}
            >
              {category.title}
            </span>
          ))}
        </div>
      )}

      {/* No results */}
      {isSearchActive && filteredCategories.length === 0 && (
        <div style={styles.noResults}>
          No results found. Try a different search term.
        </div>
      )}

      {/* Categories */}
      {filteredCategories.map(category => (
        <div
          key={category.id}
          id={`category-${category.id}`}
          style={styles.categorySection}
        >
          <h2 style={styles.categoryTitle}>{category.title}</h2>
          {category.entries.map((entry, idx) => {
            const isExpanded = expandedEntries.has(entry.id);
            const isLast = idx === category.entries.length - 1;
            return (
              <div
                key={entry.id}
                style={{
                  ...styles.entryContainer,
                  ...(isLast ? { borderBottom: 'none' } : {}),
                }}
              >
                <div
                  style={styles.entryQuestion}
                  onClick={() => handleToggle(entry.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(entry.id); } }}
                >
                  <span style={styles.entryQuestionText}>{entry.question}</span>
                  <span
                    style={{
                      ...styles.chevron,
                      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                  >
                    ▶
                  </span>
                </div>
                {isExpanded && (
                  <p style={styles.entryAnswer}>{entry.answer}</p>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
